import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getDay } from "date-fns";

interface PricingRule {
  id: string;
  name: string;
  rule_type: string;
  adjustment_type: string;
  adjustment_value: number;
  days_of_week: string[] | null;
  start_time: string | null;
  end_time: string | null;
  start_date: string | null;
  end_date: string | null;
  listing_id: string | null;
  is_active: boolean;
  priority: number;
}

export interface SlotPricing {
  time: string;
  originalPrice: number;
  adjustedPrice: number;
  discountPercent: number;
  hasDiscount: boolean;
  ruleName?: string;
  spots?: number;
  isPast?: boolean;
  /** Not past, but inside the listing's online-booking cut-off. */
  isClosed?: boolean;
  isCurrentBookingSlot?: boolean;
}

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export function timeToMinutes(timeStr: string): number {
  // Parse "9:00 AM" or "14:00" style times
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return 0;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3]?.toUpperCase();
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

export function ruleMatchesSlot(
  rule: PricingRule,
  slotTime: string,
  selectedDate: Date
): boolean {
  // Check days_of_week
  if (rule.days_of_week && rule.days_of_week.length > 0) {
    const dayName = DAY_NAMES[getDay(selectedDate)];
    if (!rule.days_of_week.includes(dayName)) return false;
  }

  // Check date range
  if (rule.start_date) {
    const ruleStart = new Date(rule.start_date);
    if (selectedDate < ruleStart) return false;
  }
  if (rule.end_date) {
    const ruleEnd = new Date(rule.end_date);
    if (selectedDate > ruleEnd) return false;
  }

  // Check time range
  if (rule.start_time || rule.end_time) {
    const slotMinutes = timeToMinutes(slotTime);
    if (rule.start_time) {
      const startMinutes = timeToMinutes(rule.start_time);
      if (slotMinutes < startMinutes) return false;
    }
    if (rule.end_time) {
      const endMinutes = timeToMinutes(rule.end_time);
      if (slotMinutes > endMinutes) return false;
    }
  }

  return true;
}

export function useDynamicPricing(
  serviceId: string | undefined,
  basePrice: number,
  timeSlots: { time: string; spots: number; isPast?: boolean; isClosed?: boolean; isCurrentBookingSlot?: boolean }[],
  selectedDate: Date | undefined
) {
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [slotPricing, setSlotPricing] = useState<SlotPricing[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRules = async () => {
      if (!serviceId) return;
      setLoading(true);

      const { data, error } = await supabase
        .from("pricing_rules")
        .select("*")
        .eq("is_active", true)
        .or(`listing_id.eq.${serviceId},listing_id.is.null`)
        .order("priority", { ascending: false });

      if (!error && data) {
        setRules(data as unknown as PricingRule[]);
      }
      setLoading(false);
    };

    fetchRules();
  }, [serviceId]);

  useEffect(() => {
    if (!selectedDate || timeSlots.length === 0) {
      setSlotPricing(timeSlots.map(({ time, isPast, isClosed, isCurrentBookingSlot }) => ({
        time,
        originalPrice: basePrice,
        adjustedPrice: basePrice,
        discountPercent: 0,
        hasDiscount: false,
        isPast,
        isClosed,
        isCurrentBookingSlot,
      })));
      return;
    }

    const pricing = timeSlots.map(({ time, spots, isPast, isClosed, isCurrentBookingSlot }) => {
      // Find highest-priority matching time-based rule
      const matchingRule = rules.find((rule) =>
        ruleMatchesSlot(rule, time, selectedDate)
      );

      let adjustedPrice = basePrice;
      let appliedRuleName = undefined;

      // Apply Admin Time-based Rules as discount
      if (matchingRule) {
        // adjustment_value is always a positive discount percentage (e.g. 20 = 20% off)
        adjustedPrice = basePrice * (1 - matchingRule.adjustment_value / 100);
        appliedRuleName = matchingRule.name;
      }

      adjustedPrice = Math.max(0, Math.round(adjustedPrice * 100) / 100);
      const discountPercent = Math.round(((basePrice - adjustedPrice) / basePrice) * 100);

      return {
        time,
        originalPrice: basePrice,
        adjustedPrice,
        discountPercent,
        hasDiscount: adjustedPrice < basePrice,
        ruleName: appliedRuleName,
        spots,
        isPast,
        isClosed,
        isCurrentBookingSlot,
      };
    });

    setSlotPricing(pricing);
  }, [rules, selectedDate, timeSlots, basePrice]);

  return { slotPricing, loading };
}
